import { createTemplate } from "@brains/plugins";
import { z } from "@brains/utils/zod";

// Schema for the AI response
export const linkExtractionSchema: z.ZodObject<{
  success: z.ZodBoolean;
  error: z.ZodString;
  title: z.ZodString;
  description: z.ZodString;
  summary: z.ZodString;
}> = z.object({
  success: z
    .boolean()
    .describe(
      "Set to true if you can extract meaningful content. Set to false only if the provided content is empty, just an error message, or otherwise unusable.",
    ),
  error: z
    .string()
    .describe(
      "If success is false, explain why content could not be extracted. Use an empty string when success is true.",
    ),
  title: z
    .string()
    .max(80)
    .describe(
      "The page title - extract from the content or create a descriptive one. Leave empty string if success is false.",
    ),
  description: z
    .string()
    .describe(
      "A one-sentence description of what the page is about. Leave empty string if success is false.",
    ),
  summary: z
    .string()
    .describe(
      "A 1-2 paragraph summary of the main content. Leave empty string if success is false.",
    ),
});

export type LinkExtractionResult = z.output<typeof linkExtractionSchema>;

export const linkExtractionTemplate: ReturnType<
  typeof createTemplate<LinkExtractionResult>
> = createTemplate<LinkExtractionResult>({
  name: "link:extraction",
  description: "Extract structured content from webpage markdown",
  dataSourceId: "shell:ai-content",
  schema: linkExtractionSchema,
  basePrompt: `You are an expert at extracting key information from webpage content.

You will receive webpage content in markdown format. Your job is to extract structured information from it.

If the content is empty, contains only an error message, or has no meaningful information to extract, set success to false and explain why.

Always include every field in your response. If success is true, set error to an empty string.

If the content has meaningful information, set success to true and extract:
1. A clear, descriptive title for the page
2. A one-sentence description of what the page is about
3. A 1-2 paragraph summary of the main content

Focus only on information present in the provided content. Do not make up or hallucinate information.

Accuracy rules:
- Do not invent dates, "recent" updates, post titles, author names, metrics, or examples.
- If you mention examples from the page, use only titles or facts explicitly present in the provided markdown.
- When a page is an index or hub, summarize the stable purpose and categories rather than speculating about every listed item.`,
  requiredPermission: "public",
});
